import clsx from "clsx";
import * as React from "react";

import type { ButtonScale } from "./Button";

export type InputBoxScale = "sm" | "md" | "lg";

export interface InputClassNameProps {
  /**
   * The scale of the input.
   * Context dependent.
   * @default "md"
   */
  scale?: InputBoxScale;

  /**
   * Whether the input display a placeholder.
   */
  placeholder?: boolean;

  /**
   * The class name to apply to the input.
   */
  className?: string | undefined;
}

export type TextInputScale = "sm" | "md" | "lg";

export interface TextInputProps
  extends React.HTMLProps<HTMLInputElement>,
    Omit<InputClassNameProps, "placeholder"> {
  /**
   * Use this prop to use the child as the input.
   */
  asChild?: boolean;
}

export interface TextAreaProps
  extends React.HTMLProps<HTMLTextAreaElement>,
    Omit<InputClassNameProps, "placeholder"> {
  /**
   * Use this prop to use the child as the input.
   */
  asChild?: boolean;
}

type Size = number | string;

export type TextInputGroupAdornment =
  | React.ReactNode
  | ((props: {
      iconSize: number;
      buttonScale: ButtonScale;
    }) => React.ReactNode);

export interface TextInputGroupProps extends React.HTMLProps<HTMLDivElement> {
  /**
   * Auto adjust adornments.
   * @default "md"
   */
  scale?: TextInputScale;

  /**
   * Adornment to be placed at the start of the input.
   */
  startAdornment?: TextInputGroupAdornment;

  /**
   * Size of the start adornment.
   */
  startAdornmentSize?: Size;

  /**
   * Adornment to be placed at the end of the input.
   */
  endAdornment?: TextInputGroupAdornment;

  textArea?: boolean;

  /**
   * Size of the end adornment.
   */
  endAdornmentSize?: Size;
}

export type TextInputEndAdornmentProps = React.HTMLProps<HTMLDivElement>;

const scales: Record<TextInputScale, string> = {
  lg: /* tw */ `text-lg py-3`,
  md: /* tw */ `text-base py-3`,
  sm: /* tw */ `text-sm py-2`,
};

const Adornment = (props: React.ComponentProps<"div">) => {
  return (
    <div
      className={clsx(
        "pointer-events-none absolute bottom-0 top-0 flex items-center justify-center [&_button]:pointer-events-auto",
        "text-text-on",
      )}
      {...props}
    />
  );
};

const getDefaultAdornmentSize = (scale: TextInputScale) => {
  switch (scale) {
    case "lg":
      return 44;
    case "md":
      return 38;
    case "sm":
      return 30;
  }
};

const getAdornmentIconSize = (scale: TextInputScale) => {
  switch (scale) {
    case "lg":
      return 18;
    case "md":
      return 16;
    case "sm":
      return 16;
  }
};

const getAdornmentButtonScale = (scale: TextInputScale): ButtonScale => {
  switch (scale) {
    case "lg":
      return "md";
    case "md":
      return "sm";
    case "sm":
      return "xs";
  }
};

const autoUnitSize = (value: Size) => {
  return typeof value === "number" ? `${value / 16}rem` : value;
};

export const TextInputGroup = React.forwardRef<
  HTMLDivElement,
  TextInputGroupProps
>((props, ref) => {
  const {
    className,
    scale = "md",
    endAdornment,
    endAdornmentSize = getDefaultAdornmentSize(scale),
    startAdornment,
    startAdornmentSize = getDefaultAdornmentSize(scale),
    children,
    style,
    ...rest
  } = props;

  const renderAdornment = (adornment: TextInputGroupAdornment) => {
    if (typeof adornment === "function") {
      return adornment({
        iconSize: getAdornmentIconSize(scale),
        buttonScale: getAdornmentButtonScale(scale),
      });
    }
    return adornment;
  };

  return (
    <div
      ref={ref}
      className={clsx("relative", className)}
      style={
        {
          ...style,
          "--end-adornment-size": endAdornment
            ? autoUnitSize(endAdornmentSize)
            : undefined,
          "--start-adornment-size": startAdornment
            ? autoUnitSize(startAdornmentSize)
            : undefined,
        } as React.CSSProperties
      }
      {...rest}
    >
      {children}
      {endAdornment && (
        <Adornment
          style={{
            right: 0,
            width: "var(--end-adornment-size)",
          }}
        >
          {renderAdornment(endAdornment)}
        </Adornment>
      )}
      {startAdornment && (
        <Adornment
          style={{
            left: 0,
            width: "var(--start-adornment-size)",
          }}
        >
          {renderAdornment(startAdornment)}
        </Adornment>
      )}
    </div>
  );
});

export const getInputClassName = ({
  className,
  scale = "md",
  placeholder,
}: InputClassNameProps) => {
  return clsx(
    className,
    placeholder ? "text-grey-on" : "text-text-on",
    "pr-[var(--end-adornment-size,0.75rem)] pl-[var(--start-adornment-size,0.75rem)]",
    "border border-text-border-light rounded transition w-full peer text-text-on",
    "placeholder:text-grey-on",
    "hover:border-primary-border",
    "focus:outline-none",
    "focus:border-primary-border w-full",
    "disabled:hover:border-inherit disabled:opacity-disabled",
    "aria-invalid:text-danger-on aria-invalid:border-danger-border",
    "aria-invalid:focus:border-danger-border",
    scales[scale],
  );
};

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (props, ref) => {
    const {
      scale = "md",
      className,
      asChild,
      children,
      style,
      ...rest
    } = props;
    const inputClassName = getInputClassName({ className, scale });
    const inputProps = {
      ref,
      className: clsx(className, inputClassName, scales[scale]),
      ...rest,
    };
    if (asChild) {
      return React.cloneElement(
        React.Children.only(props.children as React.ReactElement),
        inputProps,
      );
    }
    return <input {...inputProps} />;
  },
);

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (props, ref) => {
    const {
      scale = "md",
      className,
      asChild,
      children,
      style,
      ...rest
    } = props;
    const inputClassName = getInputClassName({ className, scale });
    const inputProps = {
      ref,
      className: clsx(className, inputClassName, scales[scale]),
      ...rest,
    };
    if (asChild) {
      return React.cloneElement(
        React.Children.only(props.children as React.ReactElement),
        inputProps,
      );
    }
    return <textarea {...inputProps} />;
  },
);

type TextInputLabel = {
  className?: string;
  children?: React.ReactNode;
  scale?: TextInputScale;
  required?: boolean;
};

const labelScales: Record<TextInputScale, string> = {
  lg: /* tw */ `text-lg py-2`,
  md: /* tw */ `text-base py-0.5`,
  sm: /* tw */ `text-sm py-0.5`,
};

export const TextInputLabel = React.forwardRef<HTMLDivElement, TextInputLabel>(
  ({ className, scale = "md", required, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          className,
          "mb-0.5 block text-text-on aria-invalid:text-danger-on",
          labelScales[scale],
        )}
        {...props}
      >
        {children}
        {required && (
          <span className="text-danger-on" aria-hidden>
            *
          </span>
        )}
      </div>
    );
  },
);
